import { ref } from "vue";
import { isEmailValid, isNPIValid, isPhoneNumberValid, isZipCodeValid } from "@/utils/validators";
import { isValidNdc } from "@/utils";
import { orderInfoService } from "@/services/orderInfoService";
import { s3KeyOf } from "@/utils/s3Links";

// Must match CreateSubmissionsBatchRequest's per-request cap in the backend (LAM-4841).
export const MAX_ADDITIONAL_MEDICATIONS = 20;

export function useOrderInfoUpdates(orderInfo) {
  const supplementalDocs = ref([]);
  const formButtonClicked = ref(true);

  // Update functions
  function updateChecklistSelection(newChecklistSelection, isDeselect = false) {
    if (isDeselect) {
      newChecklistSelection.forEach((unselectedItem) => {
        orderInfo.value.checklist = orderInfo.value.checklist.filter((item) => item.id !== unselectedItem.id);
      });
    } else {
      // Only keep items that are in the new selection (currently visible/answered in UI)
      // This ensures we don't save questions that are no longer visible or not yet answered
      const newSelectionIds = new Set(newChecklistSelection.map((item) => item.id));

      // Update existing items that are in the new selection
      const updatedChecklist = orderInfo.value.checklist
        .filter((item) => newSelectionIds.has(item.id))
        .map((item) => {
          const newItem = newChecklistSelection.find((newItem) => newItem.id === item.id);
          return newItem || item;
        });

      // Add new items that aren't in the existing checklist
      newChecklistSelection.forEach((selectedItem) => {
        if (!updatedChecklist.find((item) => item.id === selectedItem.id)) {
          updatedChecklist.push(selectedItem);
        }
      });

      orderInfo.value.checklist = updatedChecklist;
    }
  }

  function updateBasicPlusChecklistSelection(newChecklistSelection, patientChartNotes, isDeselect = false) {
    if (isDeselect) {
      newChecklistSelection.forEach((unselectedItem) => {
        orderInfo.value.basicPlusChecklist = orderInfo.value.basicPlusChecklist.filter(
          (item) => item.id !== unselectedItem.id
        );
      });
    } else {
      let updatedChecklist = [];
      newChecklistSelection.forEach((selectedItem) => {
        updatedChecklist = orderInfo.value.basicPlusChecklist.map((item) => {
          if (item.id === selectedItem.id) {
            return selectedItem;
          }
          return item;
        });
        if (!updatedChecklist.find((item) => item.id === selectedItem.id)) {
          updatedChecklist.push(selectedItem);
        }
      });
      orderInfo.value.basicPlusChecklist = updatedChecklist;
    }
  }

  function updateSignature(signature) {
    orderInfo.value.signature = signature;
  }

  function updatePrimaryDiagnosis({ icd10Code, name }) {
    orderInfo.value.primaryDiagnosisIcd10Code = icd10Code;
    orderInfo.value.primaryDiagnosisName = name;
  }

  function updateOtherDiagnosis({ index, icd10Code, name }) {
    if (!icd10Code || !name) {
      orderInfo.value.secondaryDiagnosis.splice(index, 1);
      orderInfo.value.secondaryDiagnosis.push({
        name: null,
        icd10Code: null,
      });
    } else {
      const insertIndex = orderInfo.value.secondaryDiagnosis.findIndex((item) => !item.name);
      orderInfo.value.secondaryDiagnosis[insertIndex]["icd10Code"] = icd10Code;
      orderInfo.value.secondaryDiagnosis[insertIndex]["name"] = name;
    }
  }

  function addOtherDiagnosis() {
    orderInfo.value.secondaryDiagnosis.push({ icd10Code: null, name: null });
  }

  function updateOrderInfoField(field, value) {
    orderInfo.value[field] = value;
  }

  function addFreeFormSupplementalDocs(downloadLinkInfo) {
    let doc = supplementalDocs.value[downloadLinkInfo.storeVarIndex];
    if (!doc) {
      // Push rather than index-assign: an index past the end would leave holes.
      doc = { label: null, links: [] };
      supplementalDocs.value.push(doc);
    }
    doc.links.push(downloadLinkInfo.link);
    doc.label = downloadLinkInfo.fileName;
  }

  const holdsKey = (docs, key) => (docs || []).some((doc) => (doc.links || []).some((link) => s3KeyOf(link) === key));

  // Drop every link to that S3 object, then the rows left with no links. In place, because the
  // supporting-document components hold this array through a prop.
  function removeLinkByKey(docs, key) {
    for (let index = (docs || []).length - 1; index >= 0; index -= 1) {
      const doc = docs[index];
      const kept = (doc.links || []).filter((link) => s3KeyOf(link) !== key);
      if (kept.length === (doc.links || []).length) continue;
      if (kept.length === 0) docs.splice(index, 1);
      else doc.links = kept;
    }
  }

  // Attach an autofill-only upload to the submission as a supplemental document, one row per file.
  function attachSupplementalDoc({ fileName, link }) {
    const key = s3KeyOf(link);
    if (holdsKey(supplementalDocs.value, key) || holdsKey(orderInfo.value.patientChart, key)) return;
    supplementalDocs.value.push({ label: fileName, links: [link] });
  }

  // Undo attachSupplementalDoc, wherever the row has got to: still pending here, or already merged
  // into the patient chart by a save or an autofill run.
  function detachSupplementalDoc({ link }) {
    const key = s3KeyOf(link);
    removeLinkByKey(supplementalDocs.value, key);
    removeLinkByKey(orderInfo.value.patientChart, key);
  }

  function patientChartIndex(charts, downloadLinkInfo) {
    if (downloadLinkInfo.chartEntry) return charts.indexOf(downloadLinkInfo.chartEntry);
    return downloadLinkInfo.storeVarIndex ?? -1;
  }

  function updateSupportingDocs(downloadLinkInfo) {
    if (downloadLinkInfo.storeVar === "patientChart") {
      const charts = orderInfo.value[downloadLinkInfo.storeVar];
      const entryIndex = patientChartIndex(charts, downloadLinkInfo);
      const entry = entryIndex === -1 ? undefined : charts[entryIndex];
      if (entry) {
        if (downloadLinkInfo.isDelete) {
          // Remove the specific link
          entry.links = entry.links.filter((link) => link !== downloadLinkInfo.link);
          // Remove the entire document if no links remain
          if (entry.links.length === 0) {
            charts.splice(entryIndex, 1);
          }
        } else {
          entry.links.push(downloadLinkInfo.link);
        }
      }
    } else {
      if (downloadLinkInfo.isDelete) {
        // Remove link from array
        orderInfo.value[downloadLinkInfo.storeVar] = orderInfo.value[downloadLinkInfo.storeVar].filter(
          (link) => link !== downloadLinkInfo.link
        );
      } else {
        orderInfo.value[downloadLinkInfo.storeVar]
          ? orderInfo.value[downloadLinkInfo.storeVar].push(downloadLinkInfo.link)
          : (orderInfo.value[downloadLinkInfo.storeVar] = [downloadLinkInfo.link]);
      }
    }
  }

  function addExtraMedication() {
    orderInfo.value.extraMedications.push({
      medication: "",
      expectedLengthOfTherapy: "",
      quantityPer30Days: "",
      packageSizeUnit: "",
      frequency: "",
      dailyDosage: "",
      nationalDrugCode: "",
      prescriptionNumber: "",
      // Date fields use null (not "") so they don't propagate as raw empty strings
      // into orderInfo.treatmentStartDate via removePrimaryMedication() below, which
      // the save-order endpoint now rejects with 400 (LAM-3863).
      treatmentStartDate: null,
    });
  }

  function removeExtraMedication(index) {
    orderInfo.value.extraMedications.splice(index, 1);
  }

  function removePrimaryMedication() {
    if (orderInfo.value.extraMedications.length > 0) {
      let newPrimaryMedication = orderInfo.value.extraMedications.splice(0, 1);
      orderInfo.value.medication = newPrimaryMedication[0].medication;
      orderInfo.value.expectedLengthOfTherapy = newPrimaryMedication[0].expectedLengthOfTherapy;
      orderInfo.value.quantityPer30Days = newPrimaryMedication[0].quantityPer30Days;
      orderInfo.value.packageSizeUnit = newPrimaryMedication[0].packageSizeUnit;
      orderInfo.value.dailyDosage = newPrimaryMedication[0].dailyDosage;
      orderInfo.value.nationalDrugCode = newPrimaryMedication[0].nationalDrugCode;
      orderInfo.value.treatmentStartDate = newPrimaryMedication[0].treatmentStartDate;
      orderInfo.value.frequency = newPrimaryMedication[0].frequency;
      orderInfo.value.daw = newPrimaryMedication[0].daw;
      // Falls back to "" rather than undefined: an extra medication stored before LAM-4951 has
      // no prescriptionNumber, and undefined would drop the key from the PUT body, stranding the
      // demoted medication's Rx number on the order (LAM-4951).
      orderInfo.value.prescriptionNumber = newPrimaryMedication[0].prescriptionNumber ?? "";
    } else {
      orderInfo.value.medication = "";
      orderInfo.value.expectedLengthOfTherapy = "";
      orderInfo.value.quantityPer30Days = "";
      orderInfo.value.dailyDosage = "";
      orderInfo.value.packageSizeUnit = "";
      orderInfo.value.nationalDrugCode = "";
      // Treatment start date is a DateField on the backend; null clears it cleanly,
      // "" would be rejected by save-order with 400 (LAM-3863).
      orderInfo.value.treatmentStartDate = null;
      orderInfo.value.frequency = "";
      orderInfo.value.daw = "No";
      orderInfo.value.prescriptionNumber = "";
    }
  }

  function updateExtraMedicationField(index, field, newValue) {
    orderInfo.value.extraMedications[index][field] = newValue;
  }

  // Additional medications queued for their OWN separate PA submission (LAM-4841) — distinct
  // from extraMedications above, which lists multiple drugs on ONE fax cover form/submission.
  // Each entry here becomes its own OrderInfo, sharing this order's patient/provider/insurance/
  // diagnosis/docs fields, via orderInfoService.createSubmissionsBatch at final submit time.
  // Capped to match CreateSubmissionsBatchRequest's server-side limit — otherwise the wizard
  // would let a user queue more than the backend accepts, only to have the whole batch (not
  // just the excess) rejected with a single generic 400 at final submit.
  function addAdditionalMedication() {
    if (orderInfo.value.additionalMedications.length >= MAX_ADDITIONAL_MEDICATIONS) return;
    orderInfo.value.additionalMedications.push({
      medication: "",
      expectedLengthOfTherapy: "",
      quantityPer30Days: "",
      packageSizeUnit: "",
      frequency: "",
      dailyDosage: "",
      nationalDrugCode: "",
      prescriptionNumber: "",
      treatmentStartDate: null,
      daw: "No",
    });
  }

  function removeAdditionalMedication(index) {
    orderInfo.value.additionalMedications.splice(index, 1);
  }

  function updateAdditionalMedicationField(index, field, newValue) {
    orderInfo.value.additionalMedications[index][field] = newValue;
  }

  function mergeSupplementalDocsWithPatientChart() {
    if (!Array.isArray(supplementalDocs.value)) {
      supplementalDocs.value = [];
    }

    if (!Array.isArray(orderInfo.value.patientChart)) {
      orderInfo.value.patientChart = [];
    }

    supplementalDocs.value.forEach((doc) => {
      if (doc.links?.length > 0) {
        orderInfo.value.patientChart.push({
          label: doc.label,
          links: [...doc.links],
        });
      }
    });

    supplementalDocs.value = [];
  }

  // Validation functions

  /**
   * True for a non-empty string. Treats `undefined` the same as `null`/"" — a field a data
   * source never set at all (e.g. Brooks' import builder omits providerContactFax/providerTitle
   * entirely when the provider FK doesn't resolve, rather than sending null) is `undefined`
   * after camelization, not `null`; the naive `value !== null && value.trim()` pattern used
   * throughout this file assumed every required field would at least be present-but-null, and
   * threw `TypeError: Cannot read properties of undefined (reading 'trim')` for one that wasn't —
   * aborting the whole Create Submission handler before any request was ever sent.
   */
  function hasValue(value) {
    return typeof value === "string" && value.trim() !== "";
  }

  function addressValid(field) {
    const value = orderInfo.value[field];
    return hasValue(value) && value.length <= 35;
  }

  function validPatientInfo() {
    const isCmmImport = orderInfo.value.orderSubmission?.isCmmImport;
    const requiredFields = [
      "patientFirstName",
      "patientLastName",
      "patientDob",
      "patientGender",
      "patientAddress1",
      "patientCity",
      "patientState",
      "patientZip",
      "patientMemberId",
    ];

    if (!isCmmImport) {
      requiredFields.push("benefitType");
    }

    // A fax form picked on Create Submission (LAM-5197) routes the order to FAX regardless of
    // submissionMethod, and a fax never needs a BIN — the server (_create_submission) enforces it
    // only for CoverMyMeds routing, so the form must not demand it either.
    const faxFormSelected = !!orderInfo.value.faxSubmissionFormDetail;
    if (orderInfo.value.submissionMethod === "CoverMyMeds" && !isCmmImport && !faxFormSelected) {
      requiredFields.push("bin");
    }

    return (
      requiredFields.every((field) => hasValue(orderInfo.value[field])) &&
      addressValid("patientAddress1") &&
      isZipCodeValid(orderInfo.value.patientZip)
    );
  }

  function validProviderInfo() {
    const requiredFields = [
      "providerFirstName",
      "providerLastName",
      "providerContactPhone",
      "providerContactFax",
      "providerAddress1",
      "providerCity",
      "providerState",
      "providerZip",
      "providerNpiNumber",
    ];

    const baseValid =
      requiredFields.every((field) => hasValue(orderInfo.value[field])) &&
      isNPIValid(orderInfo.value.providerNpiNumber) &&
      addressValid("providerAddress1") &&
      isZipCodeValid(orderInfo.value.providerZip) &&
      isPhoneNumberValid(orderInfo.value.providerContactPhone) &&
      isPhoneNumberValid(orderInfo.value.providerContactFax);

    if (orderInfo.value.forwardResponsesToProviderByEmail) {
      return baseValid && isEmailValid(orderInfo.value.providerEmail);
    }
    return baseValid;
  }

  function validMedicationFields(fields) {
    const isCmmImport = orderInfo.value.orderSubmission?.isCmmImport;
    const requiredFields = ["medication"];

    const numericalFields = ["expectedLengthOfTherapy", "quantityPer30Days"];

    let ndcValidation = [];
    // Package size unit and NDC are CoverMyMeds inputs; a selected fax form (LAM-5197) routes the
    // order to FAX regardless of submissionMethod, so neither is required then — same rule as the
    // BIN in validPatientInfo and the asterisks in MedicalInformationForm.
    const faxFormSelected = !!orderInfo.value.faxSubmissionFormDetail;
    if (orderInfo.value.submissionMethod === "CoverMyMeds" && !faxFormSelected) {
      requiredFields.push("packageSizeUnit");
      if (!isCmmImport) {
        ndcValidation.push("nationalDrugCode");
      }
    }

    return (
      requiredFields.every((field) => hasValue(fields[field])) &&
      numericalFields.every((field) => hasValue(fields[field]) && !isNaN(fields[field])) &&
      ndcValidation.every((field) => {
        // Same rule as the NDC field's own validator: a dashed NDC is valid input that
        // gets normalized on save, so the step must not block on it. Writers that set
        // nationalDrugCode programmatically (autofill, intake prepopulation) skip the
        // field's @change handler, so the raw dashed value can still be sitting here.
        const value = fields[field];
        return hasValue(value) && isValidNdc(value);
      })
    );
  }

  function validMedicationInfo() {
    return validMedicationFields(orderInfo.value);
  }

  function validAdditionalMedicationsInfo() {
    return (
      orderInfo.value.additionalMedications.length <= MAX_ADDITIONAL_MEDICATIONS &&
      orderInfo.value.additionalMedications.every((medication) => validMedicationFields(medication))
    );
  }

  function validDiagnosisInfo() {
    const requiredFields = ["primaryDiagnosisIcd10Code", "primaryDiagnosisName"];
    return requiredFields.every((field) => hasValue(orderInfo.value[field]));
  }

  function validSupportingDocs(required = true) {
    if (!required) return true;
    const requiredFields = ["patientChart"];
    return requiredFields.every((field) => {
      const value = orderInfo.value[field];
      return (
        value !== null &&
        Array.isArray(value) &&
        value.length > 0 &&
        value.some((doc) => doc.links && doc.links.length > 0)
      );
    });
  }

  // Save functions
  async function savePartialOrderInfo({ startAutocomplete = false } = {}) {
    if (supplementalDocs.value.some((doc) => doc.links?.length > 0)) {
      mergeSupplementalDocsWithPatientChart();
    }
    if (!orderInfo.value.orderKey) {
      const newOrder = await orderInfoService.createEmptyOrder(orderInfo.value);
      orderInfo.value.orderKey = newOrder.orderKey;
      orderInfo.value.assignees = newOrder.assignees;
      orderInfo.value.assignedGroups = newOrder.assignedGroups;
    }

    // Filter checklist to only include items with valid answers (options with id and label)
    // This ensures we don't save questions that are not currently visible/answered
    if (orderInfo.value.checklist && Array.isArray(orderInfo.value.checklist)) {
      orderInfo.value.checklist = orderInfo.value.checklist.filter(
        (item) =>
          item.options &&
          Array.isArray(item.options) &&
          item.options.length > 0 &&
          item.options.some((opt) => opt.id && opt.label)
      );
    }

    if (startAutocomplete) {
      orderInfo.value.formStep = "Review";
    }
    const payload = startAutocomplete ? { ...orderInfo.value, startAutocomplete: true } : orderInfo.value;
    const saved = await orderInfoService.saveOrder(orderInfo.value.orderKey, payload);
    if (saved?.editableSubmissionForm) {
      orderInfo.value.editableSubmissionForm = saved.editableSubmissionForm;
    }
  }

  return {
    // State
    supplementalDocs,
    formButtonClicked,

    // Update functions
    updateChecklistSelection,
    updateBasicPlusChecklistSelection,
    updateSignature,
    updatePrimaryDiagnosis,
    updateOtherDiagnosis,
    addOtherDiagnosis,
    updateOrderInfoField,
    addFreeFormSupplementalDocs,
    attachSupplementalDoc,
    detachSupplementalDoc,
    updateSupportingDocs,
    addExtraMedication,
    removeExtraMedication,
    removePrimaryMedication,
    updateExtraMedicationField,
    addAdditionalMedication,
    removeAdditionalMedication,
    updateAdditionalMedicationField,

    // Validation functions
    addressValid,
    validPatientInfo,
    validProviderInfo,
    validMedicationInfo,
    validAdditionalMedicationsInfo,
    validDiagnosisInfo,
    validSupportingDocs,

    // Utility functions
    mergeSupplementalDocsWithPatientChart,

    // Save functions
    savePartialOrderInfo,
  };
}
